import { faCheck, faCircleCheck } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  Button,
  CloseIcon,
  JoonUIColor,
  SPACING,
  Typography,
} from "@joonapp/web-shared"
import dayjs from "dayjs"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { useAddPhoneNumberModalStore } from "../../../components/modals/addPhoneNumberModal/useAddPhoneNumberModalStore"
import ParentSignupReasons from "../../../components/onboarding/ParentSignupReasons"
import useMediaQuery from "../../../hooks/useMediaQuery"
import { useUserQuery } from "../../../networking/queries"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../../util/analytics"
import {
  cohorts,
  experimentNames,
  useGetExperimentCohort,
} from "../../../util/experiments"
import { localStorageItems } from "../../../util/storage"
import { useLinkTherapistModalStore } from "../../care/therapists/useLinkTherapistModalStore"
import PaywallReferralModal, {
  usePaywallReferralModalStore,
} from "../PaywallReferralModal"
import QuarterlyOfferModal, {
  useQuarterlyOfferModalStore,
} from "../QuarterlyOfferModal"
import { useCheckoutRedirect } from "../useCheckoutRedirect"
import useGetSubOfferingCampaignPlan from "../useGetSubOfferingCampaignPlan"

const QUARTERLY_PRICE = 39.99
const ANNUAL_PRICE = 89.99
const ORIGINAL_MONTHLY_PRICE = 19.99

const PaywallNoBundling = () => {
  const [isAtTop, setIsAtTop] = useState(true)
  const { t } = useTranslation()

  const isSmallScreen = useMediaQuery("(max-height:750px)")
  const { user } = useUserQuery()

  const {
    onOpen: openPaywallReferralModal,
    hasValidCode,
    isOpen: isOpenPaywallReferralModal,
  } = usePaywallReferralModalStore()
  const { onOpen: onOpenQuarterlyOfferModal } = useQuarterlyOfferModalStore()
  const { isOpen: isOpenLinkTherapistModal } = useLinkTherapistModalStore()
  const { isOpen: isOpenAddPhoneNumberModal } = useAddPhoneNumberModalStore()
  const { isLoadingCheckoutFn, redirectToFamilyCheckout } =
    useCheckoutRedirect()

  const subOffering = user?.sub_offering as string | null | undefined
  const isGiftCard = user && subOffering === "gift-card"
  const isQuarterly = subOffering === "quarterly-0"
  const isStandard = subOffering === "standard-0"
  const isNoFreeTrial = isQuarterly || isStandard

  // Free-trial toggle experiment. Only `variant-a` on the plain default flow
  // (not gift card / quarterly / standard) shows the toggle; everyone else keeps
  // the pre-experiment behavior (default flow always gets a trial applied).
  const freeTrialToggleCohort = useGetExperimentCohort(
    experimentNames.freeTrialToggle
  )
  const isDefaultFlow = !isGiftCard && !isNoFreeTrial
  const showTrialToggle =
    isDefaultFlow && freeTrialToggleCohort === cohorts.variantA
  const [isFreeTrialEnabled, setIsFreeTrialEnabled] = useState(false)

  const onToggleFreeTrial = () => {
    setIsFreeTrialEnabled((prev) => {
      trackAnalyticEvent(ANALYTIC_EVENTS.PAYWALL_TOGGLE_FREE_TRIAL, {
        enabled: !prev,
      })
      return !prev
    })
  }

  // In the toggle experiment the toggle drives whether a trial is applied;
  // the checklist (no-trial) layout only appears when it's toggled off.
  const showTrialChecklist = showTrialToggle && !isFreeTrialEnabled
  const TRIAL_DAYS = 7

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY === 0) setIsAtTop(true)
      else setIsAtTop(false)
    }
    window.addEventListener("scroll", handleScroll)

    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const dateIn7days = dayjs().add(7, "day").format("MMMM D")
  const dateIn30days = dayjs().add(30, "day").format("MMMM D")

  const campaignPlan = useGetSubOfferingCampaignPlan()

  const getOriginalPrice = (defaultPackagePrice: number) => {
    if (defaultPackagePrice < 100) {
      return defaultPackagePrice + 40
    } else {
      return defaultPackagePrice + 30
    }
  }

  let pricePerMonth: number
  let originalPricePerMonth: number

  if (isNoFreeTrial) {
    const totalPrice = isQuarterly ? QUARTERLY_PRICE : ANNUAL_PRICE
    const months = isQuarterly ? 3 : 12
    pricePerMonth = Math.floor((totalPrice / months) * 100) / 100
    originalPricePerMonth = ORIGINAL_MONTHLY_PRICE
  } else {
    const defaultPackagePrice =
      Number((campaignPlan.price_info.unit_amount / 100).toFixed(2)) || 89.99
    const originalPrice = getOriginalPrice(defaultPackagePrice)
    pricePerMonth = Number((defaultPackagePrice / 12).toFixed(2)) - 0.01
    originalPricePerMonth = Number((originalPrice / 12).toFixed(2)) - 0.01
  }

  const onClose = () => {
    trackAnalyticEvent(ANALYTIC_EVENTS.OPEN_QUARTERLY_OFFER)
    onOpenQuarterlyOfferModal()
  }

  const handleCheckout = () => {
    // Only the toggle experiment controls the trial; other flows keep the
    // backend default (an eligible trial is applied).
    const applyTrial = showTrialToggle ? isFreeTrialEnabled : undefined
    redirectToFamilyCheckout(campaignPlan.campaign, undefined, applyTrial)
    localStorage.removeItem(localStorageItems.therapistCode)
    localStorage.setItem(localStorageItems.stripeFromPaywall, "true")
    trackAnalyticEvent(ANALYTIC_EVENTS.PAYWALL_START_TRIAL, {
      apply_trial: applyTrial,
    })
  }

  const discountedPricePerMonth = 5.83
  const finalPricePerMonth =
    hasValidCode && !isNoFreeTrial ? discountedPricePerMonth : pricePerMonth
  const discountPercentage =
    hasValidCode && !isNoFreeTrial
      ? Math.round(
          ((originalPricePerMonth - discountedPricePerMonth) /
            originalPricePerMonth) *
            100
        )
      : Math.round(
          ((originalPricePerMonth - finalPricePerMonth) /
            originalPricePerMonth) *
            100
        )

  const scrollToBottom = () =>
    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior: "smooth",
    })

  return (
    <>
      <div
        className="paywall-page"
        style={{
          filter:
            isOpenAddPhoneNumberModal ||
            isOpenLinkTherapistModal ||
            isOpenPaywallReferralModal
              ? "blur(5px)"
              : "none",
        }}
      >
        <img
          src={"/images/logos/logo_white.svg"}
          alt="Joon"
          className="joon-logo"
        />
        <button onClick={onClose} className="close-button">
          <CloseIcon color={JoonUIColor.text.disabled} size={24} />
        </button>
        <div className="paywall-title my-3">
          {t("PAYWALL_BUNDLING_CUSTOM_PLAN", { name: user?.name })}
        </div>
        <ParentSignupReasons />
        {showTrialToggle && (
          <button
            type="button"
            className="paywall-trial-toggle"
            onClick={onToggleFreeTrial}
            aria-pressed={isFreeTrialEnabled}
          >
            <span className="paywall-trial-toggle__label">
              {isFreeTrialEnabled
                ? t("PAYWALL_NB_TOGGLE_TRIAL_ENABLED", { days: TRIAL_DAYS })
                : t("PAYWALL_NB_TOGGLE_ADD_TRIAL", { days: TRIAL_DAYS })}
            </span>
            <span
              className={`paywall-trial-toggle__switch${
                isFreeTrialEnabled ? " paywall-trial-toggle__switch--on" : ""
              }`}
            >
              <span className="paywall-trial-toggle__knob" />
            </span>
          </button>
        )}
        <div className="paywall-ft-title">
          {showTrialChecklist
            ? t("PAYWALL_NB_START_PLAN_TODAY")
            : isNoFreeTrial
              ? t("PAYWALL_NB_HOW_CUSTOM")
              : t("PAYWALL_NB_HOW_TRIAL")}
        </div>
        {showTrialChecklist ? (
          <div className="paywall-checklist">
            {[
              t("PAYWALL_NB_CHECK_FULL_ACCESS"),
              t("PAYWALL_NB_CHECK_CANCEL_ANYTIME"),
              t("PAYWALL_NB_CHECK_MONEY_BACK"),
            ].map((item) => (
              <div key={item} className="paywall-checklist__item">
                <FontAwesomeIcon
                  icon={faCircleCheck}
                  color={JoonUIColor.semantic.primary}
                />
                {item}
              </div>
            ))}
          </div>
        ) : (
          <div className="paywall-content">
            <div style={{ position: "relative" }}>
              <div className="paywall-timeline">
                <div className="paywall-timeline-bg" />
                <div className="paywall-section">
                  <img
                    src="/images/icons/unlock.svg"
                    className="paywall-icon"
                    alt="bell"
                  />
                </div>
                <div className="paywall-section">
                  <img
                    src="/images/icons/bell.svg"
                    className="paywall-icon"
                    alt="bell"
                  />
                </div>
                <div style={{ height: "40px", paddingTop: "2px" }}>
                  <img
                    src="/images/icons/star.svg"
                    className="paywall-icon"
                    alt="bell"
                  />
                </div>
              </div>
              <div className="paywall-timeline-extension"></div>
            </div>
            <div className="paywall-steps">
              <div className="paywall-section">
                <div className="paywall-step-title">
                  {t("PAYWALL_TRIAL_TODAY")}
                </div>
                <div className="paywall-step-subtitle">
                  {t("PAYWALL_TRIAL_TODAY_DESC")}
                </div>
              </div>
              <div className="paywall-section">
                <div className="paywall-step-title">
                  {t("PAYWALL_NB_IN_DAYS", { days: isGiftCard ? "28" : "5" })}
                </div>
                <div className="paywall-step-subtitle">
                  {isNoFreeTrial
                    ? t("PAYWALL_NB_DAY5_TIP")
                    : t("PAYWALL_TRIAL_DAY5_DESC")}
                </div>
              </div>
              <div className="paywall-section">
                <div className="paywall-step-title">
                  {t("PAYWALL_NB_IN_DAYS", { days: isGiftCard ? "30" : "7" })}
                </div>
                <div className="paywall-step-subtitle">
                  {isNoFreeTrial
                    ? t("PAYWALL_NB_GUARANTEE")
                    : t("PAYWALL_TRIAL_DAY7_DESC", {
                        date: isGiftCard ? dateIn30days : dateIn7days,
                      })}
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="paywall-action-box">
          {isAtTop && isSmallScreen && (
            <button onClick={scrollToBottom} className="scroll-button">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="40"
                height="12"
                viewBox="0 0 40 14"
              >
                <path
                  d="M0 0 L20 12 L40 0"
                  fill="none"
                  stroke="white"
                  stroke-width="4"
                />
              </svg>
            </button>
          )}
          <div className="paywall-action-box__text">
            {showTrialToggle
              ? isFreeTrialEnabled
                ? t("PAYWALL_NB_DAYS_FREE_THEN", { days: TRIAL_DAYS })
                : t("PAYWALL_NB_INTRO_OFFER")
              : isGiftCard
                ? t("PAYWALL_NB_GUEST_PASS")
                : t("PAYWALL_NB_INTRO_OFFER")}
          </div>
          {!showTrialToggle && (
            <Typography variant="bodyBold">
              {isNoFreeTrial
                ? t("PAYWALL_NB_UNLIMITED_PREMIUM")
                : t("PAYWALL_NB_UNLIMITED_FREE", {
                    days: isGiftCard ? "30" : "7",
                  })}
            </Typography>
          )}
          <div className="paywall-price">
            <span className="paywall-price__old">${originalPricePerMonth}</span>
            <span className="paywall-price__new">${finalPricePerMonth}</span>
            <span className="paywall-price__new">
              {t("PAYWALL_NB_PER_MONTH_SAVE", { percent: discountPercentage })}
            </span>
            <span className="paywall-price__note">
              {t("PAYWALL_BILLED_ANNUALLY")}
            </span>
          </div>
          <Button
            isLoading={isLoadingCheckoutFn}
            onClick={handleCheckout}
            style={{
              fontSize: "24px",
              background: "#f39f3d",
              borderColor: "#f39f3d",
              width: "100%",
              marginTop: SPACING.space2,
            }}
            text={
              showTrialToggle ? (
                <Typography
                  variant="h2"
                  color={JoonUIColor.text.inverted}
                  style={{ fontWeight: 500 }}
                >
                  {isFreeTrialEnabled
                    ? t("PAYWALL_NB_START_FREE_TRIAL")
                    : t("PAYWALL_NB_START_MY_PLAN")}
                </Typography>
              ) : isNoFreeTrial ? (
                <Typography
                  variant="h2"
                  color={JoonUIColor.text.inverted}
                  style={{ fontWeight: 500 }}
                >
                  {t("WELCOME_GET_STARTED")}
                </Typography>
              ) : (
                <Typography
                  variant="h2"
                  color={JoonUIColor.text.inverted}
                  style={{ fontWeight: 400 }}
                >
                  {t("PAYWALL_NB_TRY_FOR")}{" "}
                  <Typography
                    variant="h2"
                    color={JoonUIColor.text.inverted}
                    style={{ fontWeight: 500 }}
                  >
                    $0.00
                  </Typography>
                </Typography>
              )
            }
          />
          <div
            className="flex flex-row items-center justify-center gap-1"
            style={{ marginTop: SPACING.space2 }}
          >
            <Typography variant="bodyBold" color={JoonUIColor.text.primary}>
              {showTrialToggle
                ? isFreeTrialEnabled
                  ? t("PAYWALL_NB_NO_PAYMENT_TODAY")
                  : t("PAYWALL_NB_CHARGED_TODAY")
                : t("PAYWALL_NO_PAYMENT_REQUIRED")}
            </Typography>
            {!showTrialToggle && (
              <FontAwesomeIcon
                icon={faCheck}
                color={JoonUIColor.text.primary}
              />
            )}
          </div>
          <button onClick={openPaywallReferralModal} className="mt-2">
            <Typography
              variant="bodyBold"
              color={JoonUIColor.semantic.info}
              style={{ textDecoration: "underline" }}
            >
              {t("PAYWALL_NB_REFERRED")}
            </Typography>
          </button>
          <div className="mt-1 mb-2" style={{ fontSize: "12px" }}>
            {t("PAYWALL_BUNDLING_OFFER_EXPIRES")}
          </div>
        </div>
      </div>
      <PaywallReferralModal />
      <QuarterlyOfferModal />
    </>
  )
}

export default PaywallNoBundling
